'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import type { CollateralExposure } from '@/components/common/CollateralExposureStack';
import type { Warning } from '@/components/common/WarningIcon';
import type { UserEarnPosition } from '@/hooks/positions/userEarnPositionsQuery';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { getParsedValue } from '@/helpers/formatters';
import { computeRewards } from '@/helpers/rewards';
import { isVaultV2RateCapped, toVaultV2ApyGross } from '@/helpers/vaultV2RateCap';
import { getVaultWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum, isApiWarningLevel } from '@/helpers/warnings';
export interface DashboardEarnPositionsTableRowsProps {
  earnPositions: UserEarnPosition[];
}
const hasPositiveRawAmount = (value?: string | null) => {
  try {
    return BigInt(value ?? 0) > 0n;
  } catch {
    return false;
  }
};
export const hasVisibleEarnPositionAssets = (position: Pick<UserEarnPosition, 'assets' | 'shares'>) => {
  return hasPositiveRawAmount(position.shares) && hasPositiveRawAmount(position.assets);
};
export const useDashboardEarnPositionsTableRows = t0 => {
  const $ = _c(2);
  const {
    earnPositions
  } = t0;
  let t1;
  if ($[0] !== earnPositions) {
    t1 = earnPositions.filter(hasVisibleEarnPositionAssets).map(_temp4).filter(isDefined);
    $[0] = earnPositions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(reward) {
  return computeRewards({
    rewardToken: {
      ...reward.asset,
      symbol: reward.asset.symbol ?? "",
      name: reward.asset.name ?? undefined,
      logoURI: reward.asset.logoUri,
      chain: {
        id: reward.asset.chainId
      }
    },
    apr: reward.supplyApr
  });
}
function _temp2(r) {
  return r.apr && r.apr > ZERO;
}
function _temp3(t0) {
  const {
    aumUsd,
    ...curator
  } = t0;
  return {
    ...curator,
    state: {
      aum: aumUsd
    }
  };
}
function _temp4(position) {
  const {
    vault
  } = position;
  const {
    logoUri,
    yieldApy,
    ...vaultAsset
  } = vault.asset;
  const asset = {
    ...vaultAsset,
    yield: yieldApy,
    symbol: vaultAsset.symbol ?? "",
    name: vaultAsset.name ?? undefined,
    logoURI: logoUri,
    id: `${vault.chainId}:${vault.asset.address.toLowerCase()}`,
    chain: {
      id: vault.chainId
    }
  };
  const parsedAsset = parseApiAsset(asset);
  const rewards = vault.rewards.map(_temp).filter(_temp2);
  const collateralExposures = vault.exposure.map(exposure => {
    const exposureAsset = exposure.collateralAsset ?? vault.asset;
    return {
      token: parseApiAsset({
        ...exposureAsset,
        symbol: exposureAsset.symbol ?? "",
        name: exposureAsset.name ?? undefined,
        logoURI: exposureAsset.logoUri,
        chain: {
          id: vault.chainId
        }
      }),
      supplyAssetsUsd: getParsedValue(exposure.exposureUsd, safeParseNumber) ?? null,
      ...(!exposure.collateralAsset && {
        isIdle: true
      })
    };
  });
  const warnings = [];
  for (const warning of vault.warnings) {
    const {
      level
    } = warning;
    if (!isApiWarningLevel(level)) {
      continue;
    }
    const message = getVaultWarningMessage({
      type: warning.type,
      level,
      metadata: {
        content: warning.content
      },
      deprecated: warning.deprecated
    });
    if (message) {
      warnings.push({
        level: apiWarningLevelToEnum(level),
        message
      });
    }
  }
  const netApy = getParsedValue(vault.apy.net, safeParseNumber);
  const performanceFee = getParsedValue(vault.performanceFee, safeParseNumber);
  const managementFee = getParsedValue(vault.managementFee, safeParseNumber);
  const isVaultV2 = vault.version === "2.0";
  const maxApy = isVaultV2 ? vault.maxApy : null;
  const grossApy = toVaultV2ApyGross({
    apyBreakdown: vault.apyBreakdown,
    netApyExcludingRewards: vault.apy.native,
    fees: {
      performanceFee: vault.performanceFee,
      managementFee: vault.managementFee
    }
  });
  const curators = vault.curators.map(_temp3);
  return {
    id: `${vault.chainId}:${vault.address}`,
    isVaultV2,
    realizedEarnings: {
      assetValue: toBigInt(position.pnlAssets),
      usdValue: getParsedValue(position.pnlUsd, safeParseNumber)
    },
    vault: {
      chainId: vault.chainId as ChainId,
      address: vault.address,
      asset,
      name: vault.name,
      curators,
      whitelisted: true,
      totalAssetsUsd: getParsedValue(vault.totalAssetsUsd, safeParseNumber),
      isGated: false,
      warnings
    },
    assets: {
      assetsUsd: getParsedValue(position.assetsUsd, safeParseNumber),
      assets: toBigInt(position.assets),
      asset: parsedAsset,
      vaultAddress: vault.address
    },
    collateralExposures: {
      collateralExposures
    },
    netApy: {
      netApy,
      apy: getParsedValue(vault.apy.native, safeParseNumber),
      performanceFee,
      managementFee,
      maxRate: getParsedValue(maxApy, safeParseNumber),
      maxApy,
      isRateCapped: isVaultV2RateCapped(grossApy, maxApy),
      includesDonations: vaultIncludesDonations(vault.chainId, vault.address),
      apyBreakdown: isVaultV2 ? vault.apyBreakdown : undefined,
      rewards,
      points: []
    }
  };
}